/**
 * In-browser port of Buckerarrestor extract_welding_data.py helpers.
 * Parses machine .txt dumps into structured rows for TLOG → S/T/C mapping.
 */

export interface BuckerarrestorDataRecord {
  voltage: number;
  current: number;
  duration: number;
}

export interface BuckerarrestorSampleRow {
  voltage: number;
  current: number;
  recordDuration: number;
  startTime: string | null;
  finishTime: string | null;
  travelSpeed: number | null;
}

export interface BuckerarrestorParsedFile {
  fileName: string;
  passName: string | null;
  records: BuckerarrestorDataRecord[];
  voltageMax: number | null;
  voltageAvg: number | null;
  voltageMin: number | null;
  currentMax: number | null;
  currentAvg: number | null;
  currentMin: number | null;
  heatInput: number | null;
  travelSpeed: number | null;
  weldLengthInches: number | null;
  finishTime: string | null;
  arcTimeSeconds: number | null;
  startTime: string | null;
  sampleRows: BuckerarrestorSampleRow[];
}

export function extractPassName(filename: string): string | null {
  const name = filename.replace(/\.txt$/i, '').replace(/ /g, '_');

  const matchConcatenated = name.match(/_([A-Z]+)(\d{1,2})_\d{8}/);
  if (matchConcatenated) {
    const passType = matchConcatenated[1];
    const passNum = matchConcatenated[2];
    if (passType === 'CAP') return `CAP ${passNum}`;
    if (passType.includes('FILL')) return `Fill ${passNum}`;
    const formatted = passType.length > 1 ? passType[0] + passType.slice(1).toLowerCase() : passType;
    return `${formatted} ${passNum}`;
  }

  const matchWithNumAndSuffix = name.match(/_([A-Z]+)_(\d{1,2})_[A-Z_]*\d{8}/);
  if (matchWithNumAndSuffix) {
    const passType = matchWithNumAndSuffix[1];
    const passNum = matchWithNumAndSuffix[2];
    if (passType.includes('FILL') || passType === 'SINGLEPASSFILL') return `Fill ${passNum}`;
    if (passType === 'CAP') return `CAP ${passNum}`;
    const formatted = passType.length > 1 ? passType[0] + passType.slice(1).toLowerCase() : passType;
    return `${formatted} ${passNum}`;
  }

  const matchSuffixWithNum = name.match(/_([A-Z]+)_[A-Z]+_(\d{1,2})_\d{8}/);
  if (matchSuffixWithNum) {
    const passType = matchSuffixWithNum[1];
    const passNum = matchSuffixWithNum[2];
    if (passType.includes('FILL') || passType === 'SINGLEPASSFILL') return `Fill ${passNum}`;
    if (passType === 'CAP') return `CAP ${passNum}`;
    const formatted = passType.length > 1 ? passType[0] + passType.slice(1).toLowerCase() : passType;
    return `${formatted} ${passNum}`;
  }

  const matchWithNum = name.match(/_([A-Z]+)_(\d{1,2})_\d{8}/);
  if (matchWithNum) {
    const passType = matchWithNum[1];
    const passNum = matchWithNum[2];
    if (passType.includes('FILL') || passType === 'SINGLEPASSFILL') return `Fill ${passNum}`;
    if (passType === 'CAP') return `CAP ${passNum}`;
    const formatted = passType.length > 1 ? passType[0] + passType.slice(1).toLowerCase() : passType;
    return `${formatted} ${passNum}`;
  }

  const matchWithSuffix = name.match(/_([A-Z]+)_([A-Z]+)_\d{8}/);
  if (matchWithSuffix) {
    const passType = matchWithSuffix[1];
    const suffix = matchWithSuffix[2];
    if (passType === 'HOTPASS') return `HOT ${suffix}`;
    const formattedType = passType.length > 1 ? passType[0] + passType.slice(1).toLowerCase() : passType;
    const formattedSuffix = suffix.length > 1 ? suffix[0] + suffix.slice(1).toLowerCase() : suffix;
    return `${formattedType} ${formattedSuffix}`;
  }

  const matchWithoutNum = name.match(/_([A-Z]+)_\d{8}/);
  if (matchWithoutNum) {
    const passType = matchWithoutNum[1];
    if (passType === 'ROOT') return 'ROOT';
    if (passType === 'HOTPASS') return 'HOT';
    return passType.length > 1 ? passType[0] + passType.slice(1).toLowerCase() : passType;
  }

  return null;
}

/** Parent folder of the .txt file → Weld ID (e.g. WPS-05-02/file.txt). */
export function extractWeldIdFromRelativePath(relativePath: string): string {
  const normalized = String(relativePath || '')
    .replace(/\\/g, '/')
    .replace(/^\/+|\/+$/g, '');
  if (!normalized.includes('/')) return '';
  const parts = normalized.split('/').filter(Boolean);
  if (parts.length < 2) return '';
  // Immediate parent of the file is the weld folder (handles nested zip roots).
  return parts[parts.length - 2] || '';
}

function paddedInt(raw: string): number {
  const stripped = String(raw || '').replace(/^0+/, '');
  if (!stripped) return 0;
  const n = parseInt(stripped, 10);
  return Number.isFinite(n) ? n : 0;
}

export function parseDataRecordLine(line: string): BuckerarrestorDataRecord | null {
  const parts = line.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return null;
  try {
    if (parts.length >= 5 && parts[0] === '0' && parts[1] === '0000') {
      const voltage = parseFloat(parts[2]);
      const current = paddedInt(parts[3]);
      const duration = parseInt(parts[4], 10);
      if (![voltage, current, duration].every(Number.isFinite)) return null;
      return { voltage, current, duration };
    }
    if (parts.length >= 4 && parts[0] === '0') {
      const voltage = parseFloat(parts[1]);
      const current = paddedInt(parts[2]);
      const duration = parseInt(parts[3], 10);
      if (![voltage, current, duration].every(Number.isFinite)) return null;
      return { voltage, current, duration };
    }
    // 3-field: voltage current duration (no leading 0)
    if (parts.length === 3 && !/^\d+$/.test(parts[0])) {
      const voltage = parseFloat(parts[0]);
      const current = paddedInt(parts[1]);
      const duration = parseInt(parts[2], 10);
      if (![voltage, current, duration].every(Number.isFinite)) return null;
      return { voltage, current, duration };
    }
  } catch {
    return null;
  }
  return null;
}

function parseLine42_43_44(line: string): { voltage: number; current: number; statType: string } | null {
  const parts = line.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 5) {
    return {
      voltage: parseFloat(parts[2]),
      current: paddedInt(parts[3]),
      statType: parts[4],
    };
  }
  if (parts.length >= 4) {
    return {
      voltage: parseFloat(parts[1]),
      current: paddedInt(parts[2]),
      statType: parts[3],
    };
  }
  return null;
}

function parseHeatInput(line: string): number | null {
  const match = line.match(/(\d+\.?\d*)\s*KJ\/inch/i);
  return match ? parseFloat(match[1]) : null;
}

function parseTravelSpeed(line: string): number | null {
  const match = line.match(/(\d+\.?\d*)\s*inch\/min/i);
  return match ? parseFloat(match[1]) : null;
}

function parseWeldLength(line: string): number | null {
  const match = line.match(/(\d+\.?\d*)\s*inch/i);
  return match ? parseFloat(match[1]) : null;
}

function parseFinishTime(line: string): string | null {
  const match = line.match(/(\d{2}:\d{2}:\d{2})/);
  return match ? match[1] : null;
}

function parseArcTime(line: string): number | null {
  const match = line.match(/ARC_TIME:\s*(\d+\.?\d*)\s*s/i);
  return match ? parseFloat(match[1]) : null;
}

export function computeStartTime(finishTimeStr: string | null, durationSeconds: number | null): string | null {
  if (!finishTimeStr || durationSeconds == null || !Number.isFinite(durationSeconds)) return null;
  const parts = finishTimeStr.split(':').map(Number);
  if (parts.length < 3 || parts.some((n) => Number.isNaN(n))) return null;
  const finishSec = parts[0] * 3600 + parts[1] * 60 + parts[2];
  let startSec = finishSec - Math.round(durationSeconds);
  // Handle midnight wrap for display only
  while (startSec < 0) startSec += 24 * 3600;
  const h = Math.floor(startSec / 3600) % 24;
  const m = Math.floor((startSec % 3600) / 60);
  const s = startSec % 60;
  return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
}

export function addSecondsToTime(timeStr: string | null, seconds: number): string | null {
  if (!timeStr) return null;
  const parts = timeStr.split(':').map(Number);
  if (parts.length < 3 || parts.some((n) => Number.isNaN(n))) return null;
  let total = parts[0] * 3600 + parts[1] * 60 + parts[2] + seconds;
  while (total < 0) total += 24 * 3600;
  const h = Math.floor(total / 3600) % 24;
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
}

function normalizeCurrentFromPadded(raw: string): number {
  const stripped = raw.replace(/^0+(?=\d)/, '') || '0';
  const n = parseInt(stripped, 10);
  return Number.isFinite(n) ? n : 0;
}

export function parseBuckerarrestorWeldingTxtContent(content: string, fileName: string): BuckerarrestorParsedFile {
  const data: BuckerarrestorParsedFile = {
    fileName,
    passName: extractPassName(fileName),
    records: [],
    voltageMax: null,
    voltageAvg: null,
    voltageMin: null,
    currentMax: null,
    currentAvg: null,
    currentMin: null,
    heatInput: null,
    travelSpeed: null,
    weldLengthInches: null,
    finishTime: null,
    arcTimeSeconds: null,
    startTime: null,
    sampleRows: [],
  };

  let collectingRecords = true;
  const lines = String(content || '').split(/\r?\n/);

  for (const line of lines) {
    const lineStripped = line.trim();
    if (!lineStripped) continue;

    if (lineStripped.toUpperCase().includes('SUMMARY')) {
      collectingRecords = false;
    } else if (collectingRecords) {
      const rec = parseDataRecordLine(lineStripped);
      if (rec && Number.isFinite(rec.voltage) && Number.isFinite(rec.current) && Number.isFinite(rec.duration)) {
        data.records.push(rec);
      }
    }

    if (lineStripped.startsWith('42')) {
      const parsed = parseLine42_43_44(lineStripped);
      if (parsed?.statType === 'MAX') {
        data.voltageMax = parsed.voltage;
        data.currentMax = parsed.current;
      }
    } else if (lineStripped.startsWith('43')) {
      const parsed = parseLine42_43_44(lineStripped);
      if (parsed?.statType === 'AVG') {
        data.voltageAvg = parsed.voltage;
        data.currentAvg = parsed.current;
      }
    } else if (lineStripped.startsWith('44')) {
      const parsed = parseLine42_43_44(lineStripped);
      if (parsed?.statType === 'MIN') {
        data.voltageMin = parsed.voltage;
        data.currentMin = parsed.current;
      }
    } else if (lineStripped.startsWith('83')) {
      const heat = parseHeatInput(lineStripped);
      if (heat != null) data.heatInput = heat;
    } else if (lineStripped.startsWith('49')) {
      const len = parseWeldLength(lineStripped);
      if (len != null) data.weldLengthInches = len;
    } else if (lineStripped.startsWith('50')) {
      const speed = parseTravelSpeed(lineStripped);
      if (speed != null) data.travelSpeed = speed;
    } else if (lineStripped.startsWith('51')) {
      const finish = parseFinishTime(lineStripped);
      if (finish) data.finishTime = finish;
    } else if (lineStripped.startsWith('48') && /ARC_TIME:/i.test(lineStripped)) {
      const arc = parseArcTime(lineStripped);
      if (arc != null) data.arcTimeSeconds = arc;
    }
  }

  if (data.finishTime && data.arcTimeSeconds != null) {
    data.startTime = computeStartTime(data.finishTime, data.arcTimeSeconds);
  }

  const fileStart = data.startTime;
  const fileTravel = data.travelSpeed;
  if (!data.records.length) {
    data.sampleRows.push({
      voltage: data.voltageAvg ?? 0,
      current: data.currentAvg ?? 0,
      recordDuration: 0,
      startTime: fileStart,
      finishTime: data.finishTime,
      travelSpeed: fileTravel,
    });
  } else {
    let prevEndSec = 0;
    for (const rec of data.records) {
      const endSec = Math.trunc(rec.duration);
      data.sampleRows.push({
        voltage: rec.voltage,
        current: rec.current,
        recordDuration: endSec,
        startTime: addSecondsToTime(fileStart, prevEndSec),
        finishTime: addSecondsToTime(fileStart, endSec),
        travelSpeed: fileTravel,
      });
      prevEndSec = endSec;
    }
  }

  return data;
}

/** Prefer most common parent folder among relative paths. */
export function detectWeldIdFromPaths(relativePaths: string[]): string {
  const counts = new Map<string, number>();
  for (const path of relativePaths) {
    const id = extractWeldIdFromRelativePath(path);
    if (!id) continue;
    counts.set(id, (counts.get(id) || 0) + 1);
  }
  let best = '';
  let bestCount = 0;
  counts.forEach((count, id) => {
    if (count > bestCount) {
      best = id;
      bestCount = count;
    }
  });
  return best;
}
